/**
 * * IMPORTANT:
 * Quotation Actions acts as a common bridge between Document and DealTable
 * page, hence any actions that cause reaction to these pages are added here.
 */

// NPM Imports
import { batch } from "react-redux";

// Internal Imports
import * as AgGridV4 from "../../mashupV4";
import store from "../../store";
import { COMMON, DOCUMENTS } from "../../constants";
import { GlobalUtils, QuotationUtils, DocumentUtils } from "../../utils";

// Action Imports
import {
  prepareDocForm,
  updateDocumentSectionAction,
  resetUndoRedoStack,
  updateComponentMeta,
  coverpageThemeAction,
} from "../DocumentActions/index";
import { fetchAndUpdateTableRows } from "../TableFieldActions/index";
import { nukeActiveEditor } from "../EditorActions/editorActions";

// Service Imports
import {
  approvalStatusCheckService,
  fetchDocRecipientListService,
} from "../DocumentActions/services";
import { fetchProductCatalogs } from "../DealTableActions/DealTableServices";
import { fetchGoogleUserInfo } from "../GoogleSheetActions/googleSheetServices";
import { getAllDocNotesByID } from "../NotesActions/notesService";
import { getSharedDocumentComments } from "../SharedDocumentActions/sharedDocumentServices";
import { getPriceListsService } from "../PricebooksActions/pricebooksService";
import { getUploadedDocMetaData } from "../BlockchainActions/blockchainServices";
import {
  fetchDealTableColumns,
  fetchDealTableRows,
  addDealTableInDocument,
  updatePriceBookInDealTable,
} from "./QuotationServices";

// Method Imports
import {
  generateDocumentDetails,
  generateDocStatusConfigs,
  updateGroupId,
  checkQuoteTableWrapper,
  checkQuoteTable,
  getFormFields,
  getFormFieldsInClause,
} from "./QuotationMethods";
import {
  prepareDocApprovalStatusData,
  prepareCollaboratorsData,
} from "../DocumentActions/method";
import { setFlagsToCatalogItems } from "../DealTableActions/DealTableMethods";

// Destructuring

// ::CONSTANTS
const { DOCUMENT_MODE, DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;
const { TABLE_TYPE, DEFAULT_CURRENCY } = COMMON;

// ::UTILS
const {
  convertDocumentContentV2,
  fillFormFieldV2,
  updateFormFieldsValueForNote,
  getTableApisTable,
} = QuotationUtils;
const { readDataFromLocalStorage } = GlobalUtils;
const {
  emptyDealTable,
  emptyQuoteTable,
  getLayoutAndComponents,
  checkSectionIsTableV2GSheet,
} = DocumentUtils;

/**
 * * INDEX
 * - addDealTableTab
 * - addDealTableToDocument
 * - clearDocumentDetails
 * - getInitData
 * - getPricebooks
 * - priceBookSelection
 * - togglePriceBookSelectionView
 * - toggleTabAndToolPanelData
 * - toggleTabView
 * - updateToolPaneData
 */

// ======================================================================

/**
 * addDealTableTab func adds dealtable tab in the document page
 */
export const addDealTableTab = () => async dispatch => {
  dispatch({
    type: "QUOTATION_ADD_DEALTABLE_TAB",
  });
};

/**
 * addDealTableSectionToDocument func attaches pricebook into the document so a dealtable can be attached to the doc.
 */
export const addDealTableSectionToDocument = () => async dispatch => {
  const state = store.getState();
  const { tableType } = state.QuotationStore;
  const { components } = state.DocumentStore;
  let tableSectionId;
  if (tableType === "DEAL_TABLE") {
    tableSectionId = Object.keys(components).find(
      componentId =>
        components[componentId] &&
        components[componentId].content &&
        components[componentId].content.type ===
          DOCUMENT_COMPONENT_TYPES.DEAL_TABLE,
    );
  } else if (tableType === "QUOTE_TABLE") {
    tableSectionId = Object.keys(components).find(
      componentId =>
        components[componentId] &&
        components[componentId].content &&
        components[componentId].content.type ===
          DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE,
    );
  }
  const wrapperSectionId = Object.keys(components).find(
    componentId =>
      components[componentId] &&
      components[componentId].content &&
      components[componentId].content.type ===
        DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE_WRAPPER,
  );

  if (tableSectionId || !wrapperSectionId) return;

  let section;
  if (tableType === "DEAL_TABLE") {
    section = emptyDealTable();
  } else if (tableType === "QUOTE_TABLE") {
    section = emptyQuoteTable();
  }
  section.id = wrapperSectionId;
  dispatch(updateDocumentSectionAction(section));
};

/**
 * addDealTableToDocument func attaches pricebook into the document so a dealtable can be attached to the doc.
 * @param {number} documentId - Refers to the document id to be fetched // 15
 * @param {number} priceListId - Refers to the pricelist id // 10
 */
export const addDealTableToDocument =
  (documentId, priceListId) => async dispatch => {
    try {
      const getAppView = readDataFromLocalStorage("appView");
      const { DealTableView } =
        getAppView && getAppView.views ? getAppView.views : {};

      const addDealTable = addDealTableInDocument(documentId, priceListId);
      const dealTableColumns = fetchDealTableColumns(DealTableView);
      const productCatalogList = fetchProductCatalogs(
        priceListId,
        "",
        "All Product Types",
        [],
      );

      return Promise.all([addDealTable, dealTableColumns, productCatalogList])
        .then(response => {
          const columnData = AgGridV4.generateGridData(response[1]);

          // Clone Pricelist & Delete pricelist inside discount and place it in sibling level
          const priceBook = { ...response[0].pricelist };
          delete response[0].pricelist;

          batch(() => {
            dispatch({
              type: "REACTION_ON_ADDING_DEALTABLE_TO_DOCUMENT",
              payload: {
                addedCatalogIds: [],
                columnDefs: columnData.columnDefs,
                dealTableId: response[0].id,
                discount: response[0],
                metaData: columnData.metaData,
                priceBook: priceBook || {},
                props: columnData.props,
                rowData: [],
                tableType: TABLE_TYPE.DEAL_TABLE,
              },
            });
            dispatch({
              type: "UPDATE_PRODUCT_CATALOG_LIST",
              payload: setFlagsToCatalogItems(response[2].PriceListItems, []),
            });
            dispatch(addDealTableSectionToDocument());
          });
          return priceBook;
        })
        .catch(err => {
          console.error("QuotationActions => addDealTableToDocument", err);
          throw new Error(err);
        });
    } catch (error) {
      throw error;
    }
  };

/**
 * clearDocumentDetails func clear all data related to document, dealtable & toolpane components.
 */
export const clearDocumentDetails = () => async dispatch => {
  dispatch({
    type: "NUKE_QUOTATION_DATA",
  });
};

export const getInitDataInternal = async (
  docID,
  documentDetails,
  isSharedDocument = false,
  sharingHash = "",
  sharedDocMetaData,
  documentPageType,
  viewMeta,
  viewData,
  ssrDocTables,
  dispatch,
) => {
  try {
    let approvalStatusData = {};
    let dealTableColumns = null;
    let dealTableID = 0;
    let dealTableRows = null;
    let showIframeView = false;
    let signatureEnvelopeID = "";
    let signatureIFrameURL = "";
    let docRecipients = [];
    let acceptorsList = [];
    const showWhoHasAccessForRecipients = true;
    let tableType = TABLE_TYPE.DEAL_TABLE;
    let isGoogleSheetAuthenticated = false;
    let lastEmailSentAt = "";

    // CHECK IF DEALTABLE EXISTS
    const isDealTableExists =
      documentDetails.deal_tables && documentDetails.deal_tables.length > 0;
    if (isDealTableExists && typeof window !== "undefined") {
      // Declarations
      dealTableID = documentDetails.deal_tables[0].id;
      let dealTableView;
      if (isSharedDocument) {
        dealTableView =
          sharedDocMetaData &&
          sharedDocMetaData.app &&
          sharedDocMetaData.app.views
            ? sharedDocMetaData.app.views.DealTableView
            : null;
        dealTableColumns = {
          VIW: {
            ...sharedDocMetaData.meta,
          },
        };
        dealTableRows = {
          ViewData: {
            ...sharedDocMetaData.child_view,
          },
        };
      } else {
        const getAppView = readDataFromLocalStorage("appView");
        dealTableView =
          getAppView && getAppView.views
            ? getAppView.views.DealTableView
            : null;
        // Executions
        dealTableColumns = await fetchDealTableColumns(dealTableView);
        dealTableRows = await fetchDealTableRows(dealTableView, dealTableID);
      }
    } else if (isDealTableExists && typeof window === "undefined") {
      dealTableID = documentDetails.deal_tables[0].id;
      dealTableColumns = {
        VIW: {
          ...viewMeta,
        },
      };
      dealTableRows = {
        ViewData: {
          ...viewData,
        },
      };
    }

    // CHECK IF QUOTETABLE EXISTS
    const isQuoteTableExists =
      documentDetails.quotes && documentDetails.quotes.length > 0;
    if (isQuoteTableExists && typeof window !== "undefined") {
      // Declarations
      dealTableID = documentDetails.quotes[0].id;
      tableType = TABLE_TYPE.QUOTE_TABLE;
      let quoteTableView;
      if (isSharedDocument) {
        quoteTableView =
          sharedDocMetaData &&
          sharedDocMetaData.app &&
          sharedDocMetaData.app.views
            ? sharedDocMetaData.app.views.QuoteTableView
            : null;
        dealTableColumns = {
          VIW: {
            ...sharedDocMetaData.meta,
          },
        };
        dealTableRows = {
          ViewData: {
            ...sharedDocMetaData.child_view,
          },
        };
      } else {
        const getAppView = readDataFromLocalStorage("appView");
        quoteTableView =
          getAppView && getAppView.views
            ? getAppView.views.QuoteTableView
            : null;
        // Executions
        dealTableColumns = await fetchDealTableColumns(quoteTableView);
        dealTableRows = await fetchDealTableRows(quoteTableView, dealTableID);
      }
    } else if (isQuoteTableExists && typeof window === "undefined") {
      dealTableID = documentDetails.quotes[0].id;
      dealTableColumns = {
        VIW: {
          ...viewMeta,
        },
      };
      dealTableRows = {
        ViewData: {
          ...viewData,
        },
      };
    }

    let documentNotes = null;
    if (
      documentPageType !== "PERMA_SHARED_DOCUMENT" &&
      typeof window !== "undefined"
    ) {
      if (isSharedDocument) {
        documentNotes = await getSharedDocumentComments(docID, sharingHash);
      } else {
        documentNotes = await getAllDocNotesByID(docID, "CONTRACT");
      }
    }

    const docStatus = documentDetails.status;
    const docStage = documentDetails.stage;
    if (
      !isSharedDocument &&
      docStage &&
      docStage !== "DRAFT" &&
      typeof window !== "undefined"
    ) {
      approvalStatusData = await approvalStatusCheckService(docID);
    }

    if (
      !isSharedDocument &&
      docStage &&
      docStage === "SENT" &&
      typeof window !== "undefined"
    ) {
      docRecipients = await fetchDocRecipientListService(docID);
      if (docRecipients) {
        const {
          collaborators,
          signers,
          acceptors,
          // eslint-disable-next-line camelcase
          last_email_sent_at,
        } = docRecipients;
        if (
          (collaborators && collaborators.length > 0) ||
          (signers && signers.length > 0) ||
          (acceptors && acceptors.length > 0)
        ) {
          docRecipients = [...collaborators, ...signers, ...acceptors];
          acceptorsList = [...acceptors];
          // eslint-disable-next-line camelcase
          lastEmailSentAt = last_email_sent_at;
        } else {
          docRecipients = [];
        }
      }
    }

    if (
      !isSharedDocument &&
      docStatus &&
      docStatus === "PREPARE_FOR_SIGNATURE"
    ) {
      signatureIFrameURL = documentDetails.tagging_view_url;
      showIframeView = true;
      signatureEnvelopeID = documentDetails.esignId;
    }
    if (documentPageType !== "PERMA_SHARED_DOCUMENT") {
      if (documentDetails.components && documentDetails.components.length > 0) {
        const found = documentDetails.components.find(
          element => element.content.type === "GoogleSheet",
        );

        if (found) {
          try {
            const googleSheetAuth = await fetchGoogleUserInfo();
            if (
              googleSheetAuth &&
              googleSheetAuth.UserInfo &&
              googleSheetAuth.UserInfo.authenticated
            ) {
              isGoogleSheetAuthenticated = true;
            }
          } catch (err) {
            console.error("err");
          }
        }
      }
    }

    return Promise.all([
      documentDetails,
      dealTableColumns,
      dealTableRows,
      approvalStatusData,
      documentNotes,
      docRecipients,
      acceptorsList,
    ]).then(response => {
      /**
       * DOCUMENT DATA MUTATIONS GOES HERE
       */

      const documentData = response[0];
      const docDetails = generateDocumentDetails(documentData);
      docDetails.blockchainMetaData = null;
      if (
        docDetails.document_flow === "ESIGN" &&
        docDetails.stage === "COMPLETED"
      ) {
        getUploadedDocMetaData(docDetails.id)
          .then(res => {
            docDetails.blockchainMetaData = res;
          })
          .catch(
            err =>
              `QuotationActions => getUploadedDocMetaData => Error : ${err}`,
          );
      }
      let docCurrency = DEFAULT_CURRENCY;
      const hasQuoteTableWrapper = checkQuoteTableWrapper(
        documentData.components_v2,
      );
      const showDealTableTabByDefault = hasQuoteTableWrapper;

      /**
       * DEALTABLE DATA MUTATIONS GOES HERE
       */
      let dealTablePayload = {};

      // DealTable/ QuoteTable is not a mandatory thing in a document, hence make the formatting only when needed
      if (isDealTableExists || isQuoteTableExists) {
        const discount =
          (documentData.deal_tables.length > 0 &&
            documentData.deal_tables[0]) ||
          {};

        // Clone Pricelist & Delete pricelist inside discount and place it in sibling level
        const priceBook = { ...discount.pricelist };
        docCurrency = priceBook.currency;
        delete discount.pricelist;

        const dealTableColumnsData = response[1];
        const dealTableRowsData = response[2];
        let gridData = null;
        if (dealTableColumnsData && dealTableRowsData) {
          gridData = AgGridV4.generateGridData(
            dealTableColumnsData,
            dealTableRowsData.ViewData.rowData,
            discount,
            tableType,
          );
        }

        dealTablePayload = {
          addedCatalogIds: gridData.addedCatalogIds || [],
          columnDefs: gridData.columnDefs || [],
          customColumnDefs: [],
          dealTableId: dealTableID,
          discount: gridData.discount || discount,
          metaData: gridData.metaData,
          priceBook: priceBook || {},
          props: gridData.props,
          rowData: gridData.rowData || [],
          showPriceBook: false,
          tableType,
        };
      } else if (
        showDealTableTabByDefault ||
        checkQuoteTable(documentData.components_v2)
      ) {
        dealTablePayload = { tableType, showPriceBook: true };
      }

      // Pick necessary keys from API
      let documentType = DOCUMENT_MODE.DOCUMENT;
      let content;
      let formFieldList = [];
      let associatedFormFieldList = [];
      const { layout, components } = getLayoutAndComponents(
        documentData.components_v2,
      );
      let componentsMapping = components;
      if (docDetails.template_id) {
        formFieldList = getFormFields(
          componentsMapping,
          docDetails.fields_used,
        );
        // This will get all df's associated with clause
        associatedFormFieldList = getFormFieldsInClause(
          componentsMapping,
          docDetails.all_fields_used,
        );
        if (formFieldList) {
          formFieldList = updateFormFieldsValueForNote(
            formFieldList,
            response[4] ? response[4].Comments : [],
          );
        }
        docDetails.fields_used = formFieldList;
        docDetails.all_fields_used = [
          ...formFieldList,
          ...associatedFormFieldList,
        ];
        documentType = DOCUMENT_MODE.TEMPLATE_DOCUMENT;
        componentsMapping = fillFormFieldV2(
          componentsMapping,
          documentType,
          documentData.fields_used,
          docCurrency,
        );
      }

      // convert raw editor data to content state
      componentsMapping = convertDocumentContentV2(
        componentsMapping,
        documentType,
      );

      if (
        docDetails.dealTable &&
        docDetails.dealTable.groups &&
        docDetails.dealTable.groups.length > 0
      ) {
        content = updateGroupId(componentsMapping, docDetails);
      }

      // Convert JSON to Draft HTML schema structure
      // Using sections instead of component, because we are adding dealtable in some cases
      const docIdOrder = [];
      const connectedGSheetSections = Object.keys(componentsMapping).filter(
        componentId =>
          checkSectionIsTableV2GSheet(components[componentId]) &&
          components[componentId].content.connected,
      );
      if (
        response[0].is_pdf_document &&
        response[0].signatories_v2 &&
        response[0].signatories_v2.length > 0
      ) {
        const { signatories_v2 } = response[0];
        let labelIndex = 1;
        for (let i = 0; i < signatories_v2.length; i += 1) {
          const { tags = [] } = signatories_v2[i];
          for (let j = 0; j < tags.length; j += 1) {
            const { component_id: cid } = tags[j];
            componentsMapping[cid] = {
              content: {
                header: "",
                id: cid,
                label: `Signature ${labelIndex}`,
                lock: false,
                type: "Signature",
                data: tags[j],
              },
            };
            labelIndex += 1;
          }
        }
      }

      // Filter out component ids from arrayOfObj => "5d94ec4d-0fe7-4078-8864-e39a79b94251"

      const isFillablePDF = documentData.pdf_form_fields
        ? true
        : documentData.is_fillable_pdf;

      // fetch table data and rows
      const tablePayload = getTableApisTable(docDetails.fields_used);
      if (typeof window === "undefined") {
        tablePayload.forEach(table => {
          if (ssrDocTables[table.table_id]) {
            table.table_rows = ssrDocTables[table.table_id];
          }
        });
      } else if (Array.isArray(tablePayload) && typeof window !== "undefined") {
        if (isSharedDocument) {
          const { doc_tables: docTables } = documentData;
          if (docTables && Object.keys(docTables).length > 0) {
            tablePayload.forEach(table => {
              table.table_rows = docTables[table.table_id];
            });
          }
        } else if (Array.isArray(tablePayload)) {
          tablePayload.forEach(table =>
            dispatch(fetchAndUpdateTableRows(table.table_id)),
          );
        }
      }
      const documentPayload = {
        content,
        docDetails,
        docIdOrder,
        docRecipients: response[5],
        acceptorsList: response[6],
        showWhoHasAccessForRecipients,
        docStatusConfigs: generateDocStatusConfigs(
          docDetails.status,
          docDetails.stage,
        ),
        hasQuoteTableWrapper,
        layout,
        components: componentsMapping,
        lastEmailSentAt,
        pdfFormFields: documentData.pdf_form_fields
          ? documentData.pdf_form_fields.pdf_form_fields
          : null,
        isFillablePDF,
        isDynamicPDF: documentData.is_dynamic_pdf
          ? documentData.is_dynamic_pdf
          : false,
        tablePayload,
      };
      const approvalsPayload = prepareDocApprovalStatusData(
        response[3].Approvals,
      );
      const collaboratorsData = prepareCollaboratorsData(response[3].Approvals);
      const pendingApprovalRequired = response[3].Response_Required;

      const notesPayload = documentNotes ? documentNotes.Comments : [];

      return {
        dispatch: {
          documentPayload,
          dealTablePayload,
          approvalsPayload,
          notesPayload,
          collaboratorsData,
          pendingApprovalRequired,
          signatureIFrameURL,
          showIframeView,
          signatureEnvelopeID,
          formFieldPayload: formFieldList,
          connectedGSheetSections,
          isGoogleSheetAuthenticated,
        },
        returnData: {
          documentPayload,
          dealTablePayload,
          approvalsPayload,
          notesPayload,
          collaboratorsData,
          pendingApprovalRequired,
          signatureIFrameURL,
          showIframeView,
          signatureEnvelopeID,
          formFieldPayload: formFieldList,
          connectedGSheetSections,
          isGoogleSheetAuthenticated,
        },
      };

      // dispatch({
      //   type: "REACTION_ON_DOCUMENT_PAGE_LOAD",
      //   payload: {
      //     documentPayload,
      //     dealTablePayload,
      //     approvalsPayload,
      //     notesPayload,
      //     collaboratorsData,
      //     pendingApprovalRequired,
      //     signatureIFrameURL,
      //     showIframeView,
      //     signatureEnvelopeID,
      //     formFieldPayload: formFieldList,
      //     connectedGSheetSections,
      //     isGoogleSheetAuthenticated,
      //   },
      // });
      // return {
      //   documentPayload,
      //   dealTablePayload,
      //   approvalsPayload,
      //   notesPayload,
      // };
    });
  } catch (error) {
    throw error;
  }
};

/**
 * getInitData func fetches document(Notes) & dealtable (column & row) data by passing document ID & Data as arguments.
 *
 * @param {number} docID - Refers to the document id to be fetched
 * @param {Object} documentDetails - Contains the document API response which is called Quotations Page.
 */
export const getInitData =
  (
    docID,
    documentDetails,
    isSharedDocument = false,
    sharingHash = "",
    sharedDocMetaData,
    documentPageType,
  ) =>
  async dispatch => {
    store.dispatch(resetUndoRedoStack());
    store.dispatch(nukeActiveEditor());
    store.dispatch(prepareDocForm([]));
    const { dispatch: dispatchData, returnData } = await getInitDataInternal(
      docID,
      documentDetails,
      isSharedDocument,
      sharingHash,
      sharedDocMetaData,
      documentPageType,
      null,
      null,
      null,
      dispatch,
    );
    dispatch({
      type: "REACTION_ON_DOCUMENT_PAGE_LOAD",
      payload: {
        ...dispatchData,
      },
    });
    return {
      ...returnData,
    };
    // try {
    //   let approvalStatusData = {};
    //   let dealTableColumns = null;
    //   let dealTableID = 0;
    //   let dealTableRows = null;
    //   let showIframeView = false;
    //   let signatureEnvelopeID = "";
    //   let signatureIFrameURL = "";
    //   let docRecipients = [];
    //   let acceptorsList = [];
    //   const showWhoHasAccessForRecipients = true;
    //   let tableType = TABLE_TYPE.DEAL_TABLE;
    //   let isGoogleSheetAuthenticated = false;
    //   let lastEmailSentAt = "";

    //   // CHECK IF DEALTABLE EXISTS
    //   const isDealTableExists =
    //     documentDetails.deal_tables && documentDetails.deal_tables.length > 0;

    //   if (isDealTableExists && typeof window !== "undefined") {
    //     // Declarations
    //     dealTableID = documentDetails.deal_tables[0].id;
    //     let dealTableView;
    //     if (isSharedDocument) {
    //       dealTableView =
    //         sharedDocMetaData &&
    //         sharedDocMetaData.app &&
    //         sharedDocMetaData.app.views
    //           ? sharedDocMetaData.app.views.DealTableView
    //           : null;
    //       dealTableColumns = {
    //         VIW: {
    //           ...sharedDocMetaData.meta,
    //         },
    //       };
    //       dealTableRows = {
    //         ViewData: {
    //           ...sharedDocMetaData.child_view,
    //         },
    //       };
    //     } else {
    //       const getAppView = readDataFromLocalStorage("appView");
    //       dealTableView =
    //         getAppView && getAppView.views
    //           ? getAppView.views.DealTableView
    //           : null;
    //       // Executions
    //       dealTableColumns = await fetchDealTableColumns(dealTableView);
    //       dealTableRows = await fetchDealTableRows(dealTableView, dealTableID);
    //     }
    //   }

    //   // CHECK IF QUOTETABLE EXISTS
    //   const isQuoteTableExists =
    //     documentDetails.quotes && documentDetails.quotes.length > 0;

    //   if (isQuoteTableExists) {
    //     // Declarations
    //     dealTableID = documentDetails.quotes[0].id;
    //     tableType = TABLE_TYPE.QUOTE_TABLE;
    //     let quoteTableView;
    //     if (isSharedDocument) {
    //       quoteTableView =
    //         sharedDocMetaData &&
    //         sharedDocMetaData.app &&
    //         sharedDocMetaData.app.views
    //           ? sharedDocMetaData.app.views.QuoteTableView
    //           : null;
    //       dealTableColumns = {
    //         VIW: {
    //           ...sharedDocMetaData.meta,
    //         },
    //       };
    //       dealTableRows = {
    //         ViewData: {
    //           ...sharedDocMetaData.child_view,
    //         },
    //       };
    //     } else {
    //       const getAppView = readDataFromLocalStorage("appView");
    //       quoteTableView =
    //         getAppView && getAppView.views
    //           ? getAppView.views.QuoteTableView
    //           : null;
    //       // Executions
    //       dealTableColumns = await fetchDealTableColumns(quoteTableView);
    //       dealTableRows = await fetchDealTableRows(quoteTableView, dealTableID);
    //     }
    //   }

    //   let documentNotes = null;
    //   if (
    //     documentPageType !== "PERMA_SHARED_DOCUMENT" &&
    //     typeof window !== "undefined"
    //   ) {
    //     if (isSharedDocument) {
    //       documentNotes = await getSharedDocumentComments(docID, sharingHash);
    //     } else {
    //       documentNotes = await getAllDocNotesByID(docID, "CONTRACT");
    //     }
    //   }

    //   const docStatus = documentDetails.status;
    //   const docStage = documentDetails.stage;
    //   if (!isSharedDocument && docStage && docStage !== "DRAFT") {
    //     approvalStatusData = await approvalStatusCheckService(docID);
    //   }

    //   if (!isSharedDocument && docStage && docStage === "SENT") {
    //     docRecipients = await fetchDocRecipientListService(docID);
    //     if (docRecipients) {
    //       const {
    //         collaborators,
    //         signers,
    //         acceptors,
    //         // eslint-disable-next-line camelcase
    //         last_email_sent_at,
    //       } = docRecipients;
    //       if (
    //         (collaborators && collaborators.length > 0) ||
    //         (signers && signers.length > 0) ||
    //         (acceptors && acceptors.length > 0)
    //       ) {
    //         docRecipients = [...collaborators, ...signers, ...acceptors];
    //         acceptorsList = [...acceptors];
    //         // eslint-disable-next-line camelcase
    //         lastEmailSentAt = last_email_sent_at;
    //       } else {
    //         docRecipients = [];
    //       }
    //     }
    //   }

    //   if (
    //     !isSharedDocument &&
    //     docStatus &&
    //     docStatus === "PREPARE_FOR_SIGNATURE"
    //   ) {
    //     signatureIFrameURL = documentDetails.tagging_view_url;
    //     showIframeView = true;
    //     signatureEnvelopeID = documentDetails.esignId;
    //   }
    //   if (documentPageType !== "PERMA_SHARED_DOCUMENT") {
    //     if (
    //       documentDetails.components &&
    //       documentDetails.components.length > 0
    //     ) {
    //       const found = documentDetails.components.find(
    //         element => element.content.type === "GoogleSheet",
    //       );

    //       if (found) {
    //         try {
    //           const googleSheetAuth = await fetchGoogleUserInfo();
    //           if (
    //             googleSheetAuth &&
    //             googleSheetAuth.UserInfo &&
    //             googleSheetAuth.UserInfo.authenticated
    //           ) {
    //             isGoogleSheetAuthenticated = true;
    //           }
    //         } catch (err) {
    //           console.error("err");
    //         }
    //       }
    //     }
    //   }

    //   return Promise.all([
    //     documentDetails,
    //     dealTableColumns,
    //     dealTableRows,
    //     approvalStatusData,
    //     documentNotes,
    //     docRecipients,
    //     acceptorsList,
    //   ]).then(response => {
    //     /**
    //      * DOCUMENT DATA MUTATIONS GOES HERE
    //      */

    //     const documentData = response[0];
    //     const docDetails = generateDocumentDetails(documentData);
    //     let docCurrency = DEFAULT_CURRENCY;
    //     const hasQuoteTableWrapper = checkQuoteTableWrapper(
    //       documentData.components_v2,
    //     );
    //     const showDealTableTabByDefault = hasQuoteTableWrapper;

    //     /**
    //      * DEALTABLE DATA MUTATIONS GOES HERE
    //      */
    //     let dealTablePayload = {};

    //     // DealTable/ QuoteTable is not a mandatory thing in a document, hence make the formatting only when needed
    //     if (
    //       typeof window !== "undefined" &&
    //       (isDealTableExists || isQuoteTableExists)
    //     ) {
    //       const discount =
    //         (documentData.deal_tables.length > 0 &&
    //           documentData.deal_tables[0]) ||
    //         {};

    //       // Clone Pricelist & Delete pricelist inside discount and place it in sibling level
    //       const priceBook = { ...discount.pricelist };
    //       docCurrency = priceBook.currency;
    //       delete discount.pricelist;

    //       const dealTableColumnsData = response[1];
    //       const dealTableRowsData = response[2];
    //       let gridData = null;
    //       if (dealTableColumnsData && dealTableRowsData) {
    //         gridData = AgGridV4.generateGridData(
    //           dealTableColumnsData,
    //           dealTableRowsData.ViewData.rowData,
    //           discount,
    //           tableType,
    //         );
    //       }

    //       dealTablePayload = {
    //         addedCatalogIds: gridData.addedCatalogIds || [],
    //         columnDefs: gridData.columnDefs || [],
    //         customColumnDefs: [],
    //         dealTableId: dealTableID,
    //         discount: gridData.discount || discount,
    //         metaData: gridData.metaData,
    //         priceBook: priceBook || {},
    //         props: gridData.props,
    //         rowData: gridData.rowData || [],
    //         showPriceBook: false,
    //         tableType,
    //       };
    //     } else if (
    //       showDealTableTabByDefault ||
    //       checkQuoteTable(documentData.components_v2)
    //     ) {
    //       dealTablePayload = { tableType, showPriceBook: true };
    //     }

    //     // Pick necessary keys from API
    //     let documentType = DOCUMENT_MODE.DOCUMENT;
    //     let content;
    //     let formFieldList = [];
    //     const { layout, components } = getLayoutAndComponents(
    //       documentData.components_v2,
    //     );
    //     let componentsMapping = components;
    //     if (docDetails.template_id) {
    //       formFieldList = getFormFields(
    //         componentsMapping,
    //         docDetails.fields_used,
    //       );
    //       if (formFieldList) {
    //         formFieldList = updateFormFieldsValueForNote(
    //           formFieldList,
    //           response[4] ? response[4].Comments : [],
    //         );
    //       }
    //       docDetails.fields_used = formFieldList;
    //       documentType = DOCUMENT_MODE.TEMPLATE_DOCUMENT;
    //       componentsMapping = fillFormFieldV2(
    //         componentsMapping,
    //         documentType,
    //         documentData.fields_used,
    //         docCurrency,
    //       );
    //     }

    //     // convert raw editor data to content state
    //     componentsMapping = convertDocumentContentV2(
    //       componentsMapping,
    //       documentType,
    //     );

    //     if (
    //       docDetails.dealTable &&
    //       docDetails.dealTable.groups &&
    //       docDetails.dealTable.groups.length > 0
    //     ) {
    //       content = updateGroupId(componentsMapping, docDetails);
    //     }

    //     // Convert JSON to Draft HTML schema structure
    //     // Using sections instead of component, because we are adding dealtable in some cases
    //     const docIdOrder = [];
    //     const connectedGSheetSections = Object.keys(componentsMapping).filter(
    //       componentId =>
    //         checkSectionIsTableV2GSheet(components[componentId]) &&
    //         components[componentId].content.connected,
    //     );
    //     if (
    //       response[0].is_pdf_document &&
    //       response[0].signatories_v2 &&
    //       response[0].signatories_v2.length > 0
    //     ) {
    //       const { signatories_v2 } = response[0];
    //       let labelIndex = 1;
    //       for (let i = 0; i < signatories_v2.length; i += 1) {
    //         const { tags = [] } = signatories_v2[i];
    //         for (let j = 0; j < tags.length; j += 1) {
    //           const { component_id: cid } = tags[j];
    //           componentsMapping[cid] = {
    //             content: {
    //               header: "",
    //               id: cid,
    //               label: `Signature ${labelIndex}`,
    //               lock: false,
    //               type: "Signature",
    //               data: tags[j],
    //             },
    //           };
    //           labelIndex += 1;
    //         }
    //       }
    //     }

    //     // Filter out component ids from arrayOfObj => "5d94ec4d-0fe7-4078-8864-e39a79b94251"

    //     const isFillablePDF = documentData.pdf_form_fields
    //       ? true
    //       : documentData.is_fillable_pdf;
    //     const documentPayload = {
    //       content,
    //       docDetails,
    //       docIdOrder,
    //       docRecipients: response[5],
    //       acceptorsList: response[6],
    //       showWhoHasAccessForRecipients,
    //       docStatusConfigs: generateDocStatusConfigs(
    //         docDetails.status,
    //         docDetails.stage,
    //       ),
    //       hasQuoteTableWrapper,
    //       layout,
    //       components: componentsMapping,
    //       lastEmailSentAt,
    //       pdfFormFields: documentData.pdf_form_fields
    //         ? documentData.pdf_form_fields.pdf_form_fields
    //         : null,
    //       isFillablePDF,
    //       isDynamicPDF: documentData.is_dynamic_pdf
    //         ? documentData.is_dynamic_pdf
    //         : false,
    //     };
    //     const approvalsPayload = prepareDocApprovalStatusData(
    //       response[3].Approvals,
    //     );
    //     const collaboratorsData = prepareCollaboratorsData(
    //       response[3].Approvals,
    //     );
    //     const pendingApprovalRequired = response[3].Response_Required;

    //     const notesPayload = documentNotes ? documentNotes.Comments : [];

    //     console.debug("REACTION_ON_DOCUMENT_PAGE_LOAD");

    //     dispatch({
    //       type: "REACTION_ON_DOCUMENT_PAGE_LOAD",
    //       payload: {
    //         documentPayload,
    //         dealTablePayload,
    //         approvalsPayload,
    //         notesPayload,
    //         collaboratorsData,
    //         pendingApprovalRequired,
    //         signatureIFrameURL,
    //         showIframeView,
    //         signatureEnvelopeID,
    //         formFieldPayload: formFieldList,
    //         connectedGSheetSections,
    //         isGoogleSheetAuthenticated,
    //       },
    //     });
    //     return {
    //       documentPayload,
    //       dealTablePayload,
    //       approvalsPayload,
    //       notesPayload,
    //     };
    //   });
    // } catch (error) {
    //   throw error;
    // }
  };

/**
 * getPricebooks func fetches all active pricebooks of the org.
 */
export const getPricebooks = () => dispatch =>
  new Promise((resolve, reject) => {
    getPriceListsService()
      .then(response => {
        const activePriceList = response.PriceListPage.plists.filter(
          list => list.active,
        );
        dispatch({
          type: "FETCH_PRICELIST_ON_POPUP",
          payload: activePriceList,
        });
        resolve(activePriceList);
      })
      .catch(error => {
        console.error("QuotationActions => getPricebooks", error);
        reject(error);
      });
  });

/**
 * priceBookSelection func updates the pricebook attached to a dealtable under the document.
 */
export const priceBookSelection =
  ({ dealTableId, documentId, priceListId, isPriceBookAttached }) =>
  async dispatch => {
    if (!isPriceBookAttached) {
      return dispatch(addDealTableToDocument(documentId, priceListId));
    }

    try {
      const dealTableWithNewPriceBookData = updatePriceBookInDealTable(
        dealTableId,
        priceListId,
      );
      const productCatalogList = fetchProductCatalogs(
        priceListId,
        "",
        "All Product Types",
        [],
      );

      return await Promise.all([
        dealTableWithNewPriceBookData,
        productCatalogList,
      ])
        .then(response => {
          // Clone Pricelist & Delete pricelist inside discount and place it in sibling level
          const priceBook = { ...response[0].pricelist };
          delete response[0].pricelist;

          batch(() => {
            dispatch({
              type: "REACTION_ON_CHANGING_PRICEBOOK_IN_DEALTABLE",
              payload: { dealTableId, discount: response[0], priceBook },
            });
            dispatch({
              type: "UPDATE_PRODUCT_CATALOG_LIST",
              payload: setFlagsToCatalogItems(response[1].PriceListItems, []),
            });
            dispatch({
              type: "QUOTATION_TOGGLE_PRICEBOOK_SELECTION_VIEW",
            });
          });

          return priceBook;
        })
        .catch(error => {
          console.error("QuotationActions => priceBookSelection", error);
          throw error;
        });
    } catch (error) {
      throw error;
    }
  };

/**
 * togglePriceBookSelectionView func toggles the view between listing all the pricebook & selecting the pricebook under product catalog screen.
 */
export const togglePriceBookSelectionView = () => async dispatch => {
  dispatch({
    type: "QUOTATION_TOGGLE_PRICEBOOK_SELECTION_VIEW",
  });
};

/**
 * toggleTabAndToolPanelData func is used to change the active tab & tool pane view according based on the active tab.
 */
export const toggleTabAndToolPanelData =
  ({
    dealTableId,
    isOpen,
    page,
    panelView,
    tabView,
    tabViewType,
    googleSheetId,
    excelSheetId,
    showPriceBook,
    tableType,
    tableId,
  }) =>
  async dispatch => {
    batch(() => {
      dispatch({
        type: "TOGGLE_QUOTATION_TAB_VIEW",
        payload: {
          view: tabView,
          dealTableId,
          tabViewType,
          googleSheetId,
          excelSheetId,
          showPriceBook,
          tableType,
          tableId,
        },
      });
      dispatch({
        type: "UPDATE_TOOLPANE_DATA",
        payload: {
          view: panelView,
          page,
          isOpen,
        },
      });
      dispatch({ type: "NUKE_ACTIVE_EDITOR" });
    });
  };

/**
 * toggleTabView func decides which component has to be shown when clicked on the tab option.
 *
 * @param {string} view - Hold the name of the view to be displayed.å
 */
export const toggleTabView = (view, dealTableId) => async dispatch => {
  dispatch({
    type: "TOGGLE_QUOTATION_TAB_VIEW",
    payload: {
      view,
      dealTableId,
    },
  });
};

/**
 * updateToolPaneData func controls the toolpane actions, here all the 3
 * params can be optional & the handling is done in toolpane reducer.
 *
 * @param {boolean} isOpen - Decides toolpane should show the content, like catalog, notes, etc
 * @param {string} view - Decides which app content HTML should be shown, like catalog, notes, etc
 * @param {string} page - Based on page, the app list will change
 */
export const updateToolPaneData =
  (isOpen, view, page, errHandling) => async dispatch => {
    dispatch({
      type: "UPDATE_TOOLPANE_DATA",
      payload: {
        view,
        page,
        isOpen,
        errHandling,
      },
    });
  };

export const updateSidePaneUIAction = () => dispatch => {
  dispatch({
    type: "UPDATE_SIDE_PANE_UI",
    payload: {},
  });
};

export const setSidePaneDataForPageNo = payload => dispatch => {
  const addComponentType = "PageNumber";
  const sectionDataForPageNumber = payload.sectionData;
  const isOpen = typeof payload.isOpen === "boolean" ? payload.isOpen : true;

  dispatch({
    type: "UPDATE_TOOLPANE_DATA_FOR_PAGENUMBER",
    payload: {
      sectionDataForPageNumber,
      addComponentType,
      isOpen,
    },
  });
};

/**
 * setSectionDataForRightToolPane func provide section data for the toolpane(Signature) actions
 */
export const setSectionDataForActiveSignatureInRightToolPane =
  sectionData => dispatch => {
    dispatch({
      type: "UPDATE_TOOLPANE_DATA_FOR_SIGNATURE",
      payload: {
        sectionData,
      },
    });
  };

// the below function will be used to provide section data for the toolpane

export const setSectionDataForActivePaymentButtonInRightToolPane =
  sectionData => dispatch => {
    dispatch({
      type: "UPDATE_TOOLPANE_DATA_FOR_STRIPE_PAYMENT",
      payload: {
        sectionData,
      },
    });
  };

export const handleGSheetAuth = value => dispatch => {
  dispatch({
    type: "UPDATE_GOOGLE_SHEET_AUTH",
    payload: {
      isGoogleSheetAuthenticated: value || false,
    },
  });
};

/**
 * update document clicked component id
 */

export const updateDocumentClickedComponentID = (id, flag, type) => dispatch =>
  dispatch({
    type: "UPDATE_DOCUMENT_CLICKED_COMPONENT_ID",
    payload: {
      clickedActiveComponentId: id,
      preventClickedComponentIdUpdate: flag,
      clickedActiveComponentType: type,
    },
  });

export const handleClickOutsideDocumnet = documentViewRef => dispatch => {
  const state = store.getState();
  const {
    clickedActiveComponentId,
    clickedActiveComponentType,
    preventClickedComponentIdUpdate,
    coverpageThemeActionLabel,
  } = state.DocumentStore;

  if (clickedActiveComponentId) {
    if (clickedActiveComponentType === "Signature") {
      dispatch(setSectionDataForActiveSignatureInRightToolPane({}));
      dispatch(updateDocumentClickedComponentID(null, true, null));
    } else if (clickedActiveComponentType === "StripePayment") {
      dispatch(setSectionDataForActivePaymentButtonInRightToolPane({}));
      dispatch(updateDocumentClickedComponentID(null, true, null));
    } else if (clickedActiveComponentType === "Image") {
      dispatch(updateDocumentClickedComponentID(null, true, null));
    } else if (preventClickedComponentIdUpdate === false) {
      // Here, need to update the text and table's local editor state with latest
      // editor state from the editor store. This case will be called when applying the color
      // from the custom color dropdown and click outside
      dispatch(updateDocumentClickedComponentID(null, true, null));
      dispatch(updateComponentMeta({}));
      if (
        clickedActiveComponentType === "HTML" ||
        (clickedActiveComponentType === "Table" && clickedActiveComponentId)
      ) {
        const { activeEditorState, activeComponentInfo } = state.EditorStore;
        if (activeEditorState) {
          let flag = false;
          if (
            activeComponentInfo &&
            (activeComponentInfo.tableCellId ||
              activeComponentInfo.componentId) === clickedActiveComponentId
          ) {
            flag = true;
          }
          dispatch(nukeActiveEditor(flag, flag));
        }
      }

      // Here, we'll have to nuke the editor store and remove the focus
      // from editor in case of Text and Table Component
      // usecase :=> (ex: applying colors from the toolbar's color-picker)
      if (
        clickedActiveComponentType === "HTML" ||
        clickedActiveComponentType === "Table"
      ) {
        if (documentViewRef && documentViewRef.current) {
          documentViewRef.current.focus({
            preventScroll: true,
          });
        }
      }
    } else if (
      clickedActiveComponentType === "Table" &&
      coverpageThemeActionLabel !== null
    ) {
      // To close any right pannel ex. table formula pane
      dispatch(updateDocumentClickedComponentID(null, true, null));
      dispatch(updateComponentMeta({}));
      dispatch(coverpageThemeAction(null));
      dispatch({ type: "NUKE_ACTIVE_EDITOR" });
    }
  }
};
